import React, { useEffect, useState } from "react";
import { Button, Modal, Checkbox, Typography, message, Image, Input } from "antd";
import { Loading } from "./Loading";

import { getPreSignedUrls, uploadFileToS3 } from "../utils/extraFunctions";
import { EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { FaRegFilePdf } from "react-icons/fa";

const { Text } = Typography;
const { confirm } = Modal;
const MultipleUpload = ({ showViewFileModal, setShowViewFileModal, files: incomingFiles, loading, setLoading, fileInfo, setSelectedFiles, setFileInfo, setDuplicateUpload, duplicateUpload, uploadTemp, uploadType, handleUploadFunc, showInputFileName, origin }) => {
  const [localFiles, setLocalFiles] = useState([]);
  const [editingFileName, setEditingFileName] = useState(null);
  const [editedNameValue, setEditedNameValue] = useState("");
  const anyFileSelected = localFiles.some((file) => file.selected);

  const [selectedChecklists, setSelectedChecklists] = useState({});

    const checkboxOptions = [
        { label: "Buyer", value: "BUYER" },
        { label: "Buyer Agent", value: "BUYER_AGENT" },
        { label: "Seller", value: "SELLER" },
        { label: "Seller Agent", value: "SELLER_AGENT" },
      ];

      const handleCheckboxChange = (fileName, checkedValues) => {
        setSelectedChecklists((prev) => ({
          ...prev,
          [fileName]: checkedValues,
        }));
      };

  useEffect(() => {
    if (showViewFileModal && incomingFiles?.length) {
      const initialized = incomingFiles.map((file) => ({
        ...file,
        selected: true,
        originalName: file.name, // ✅ store the original name separately
      }));
      setLocalFiles(initialized);
    }
  }, [showViewFileModal, incomingFiles]);

  const handleCancelFileView = () => {
     confirm({
                title: `Are you sure you don't want to upload these files? `,
                okText: "Yes",
                cancelText: "No",
                icon: <ExclamationCircleOutlined />,
                content: "",
                onOk() {
                  setShowViewFileModal(false);
                  setSelectedFiles([]);
                },
                onCancel() {},
            });
    
  };

  const MAX_FILE_SIZE_MB = 100;
  const totalSizeMB = localFiles?.filter((file) => file.selected).reduce((total, file) => total + (file?.size || 0), 0) / (1024 * 1024);
  const isFileSizeExceeded = totalSizeMB > MAX_FILE_SIZE_MB;

  const handleOkFileView = async () => {
    try {
      setLoading(true);
      const selectedFiles = localFiles.filter((file) => file.selected);

      let updatedFileInfo = fileInfo?.filter(
        (fileData) => fileData?.file instanceof File && selectedFiles.some((f) => f.originalName === fileData.filename) // ✅ match with originalName
      );

      let filenames = updatedFileInfo?.map((fileData) => fileData.filename);

      if (filenames?.length === 0) {
        message.error("No valid files to upload.");
        setShowViewFileModal(false);
        setLoading(false);
        return;
      }

      const preSignedUrls = await getPreSignedUrls(filenames);
      if (!preSignedUrls) {
        message.error("No valid files to upload.");
        setShowViewFileModal(false);
        setLoading(false);
        return;
      }

      const uploadedFilesInfo = [];

      for (const fileData of updatedFileInfo) {
        const { url: uploadUrl, fileName } = preSignedUrls?.find((item) => item?.fileName === fileData?.filename) || {};

        if (!uploadUrl) {
          message.error(`URL not found for file: ${fileData?.filename}`);
          continue;
        }

        const finalFileLink = await uploadFileToS3(fileData?.file, uploadUrl);
        if (finalFileLink) {
          let fileType;
          if (fileData?.file?.type === "application/pdf") {
            fileType = "PDF";
          } else if (fileData?.file?.type?.startsWith("image/")) {
            fileType = "IMAGE";
          }

          // ✅ Use edited name from localFiles if available
          const matchingLocal = selectedFiles.find((f) => f.originalName === fileData.filename);
          uploadedFilesInfo.push({
            fileName: matchingLocal?.name || fileName,
            fileLink: finalFileLink,
            ...((origin === "checklistTable" && selectedChecklists) ? {signatureOf: selectedChecklists[matchingLocal?.name] || selectedChecklists[fileName]} : {}),
            ...(fileType && { fileType }),
          });
        }
      }
      uploadTemp(uploadedFilesInfo);
      
    } catch (error) {
      message.error("An unexpected error occurred during file upload.");
      setLoading(false);
    }finally{
      
      setLoading(false);
      setShowViewFileModal(false);
      setSelectedFiles([]);
      setLocalFiles([]);
      setFileInfo([]);
    }
  };

  const handleEdit = (file) => {
    setEditingFileName(file.name);
    setEditedNameValue(file.name);
  };

  const handleSaveEditedName = (file) => {
    const updatedFiles = localFiles.map((f) => {
      if (f.originalName === file.originalName) {
        // match using originalName
        return { ...f, name: editedNameValue };
      }
      return f;
    });

    setLocalFiles(updatedFiles);
    setEditingFileName(null);
    setEditedNameValue("");
  };

  return (
    <Modal
      title={<div style={{ width: "100%", textAlign: "center" ,fontSize:"18px"}}>Upload Files</div>}
      open={showViewFileModal}
      onOk={handleOkFileView}
      onCancel={handleCancelFileView}
      bodyStyle={{ maxHeight: "400px", overflowY: "auto" }}
      footer={
        loading
          ? null
          : [
              <div style={{ display: "flex", flexDirection: "column", width: "100%" }} key="footer">
                {isFileSizeExceeded && (
                  <Text type="danger" style={{ marginBottom: "10px", textAlign: "center" }}>
                    Total file size exceeded **100 MB**. Please remove some files.
                  </Text>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <span>
                    <strong>Total Size:</strong> {totalSizeMB.toFixed(2)} MB
                  </span>
                  <div>
                    <Button onClick={handleCancelFileView}>Cancel</Button>
                    <Button onClick={handleOkFileView} type="primary" disabled={isFileSizeExceeded || !anyFileSelected || loading}>
                      OK
                    </Button>
                  </div>
                </div>
              </div>,
            ]
      }
      maskClosable={!loading}
      closable={!loading}
    >
      {loading ? (
        <Loading />
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "20px",
            padding: "10px",
          }}
        >
          {localFiles.map((file, index) => (
            <div 
            style={{
              width: "100%",
              maxWidth: "520px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection:"column"
            }}
            >
            <div
              key={index}
              style={{
                width: "100%",
                maxWidth: "520px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                marginBottom: "5px",
              }}
            >
              {file?.type?.startsWith("image/") || uploadType === "IMAGE" ? <Image src={file?.url} width={40} height={40} style={{ objectFit: "cover" }} preview={true} /> : <FaRegFilePdf style={{ color: "red", marginRight: "5px", fontSize: "22px" }} />}

              <div style={{ flex: 1, marginLeft: "15px", overflow: "hidden" }}>
                {editingFileName === file.name ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px", width: "95%" }}>
                    <Input value={editedNameValue} onChange={(e) => setEditedNameValue(e.target.value)} style={{ flex: 1 }} />
                    <Button type="primary" onClick={() => handleSaveEditedName(file)} style={{ padding: "0 10px" }}>
                      Done
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {file?.name}
                    </div>
                    <div style={{ fontSize: "12px", color: "#888" }}>{(file?.size / (1024 * 1024)).toFixed(2)} MB</div>
                  </>
                )}
              </div>

              {editingFileName !== file.name && <EditOutlined style={{ marginRight: "20px", cursor: "pointer" }} onClick={() => handleEdit(file)} />}

              <Checkbox
                checked={file.selected}
                onChange={(e) => {
                  const updatedFiles = localFiles.map((f) => (f.name === file.name ? { ...f, selected: e.target.checked } : f));
                  setLocalFiles(updatedFiles);
                }}
              />
              </div>
              {origin === "checklistTable" && (
                <div style={{ marginTop: "10px", display: "flex" }}>
                  <Checkbox.Group
                    options={checkboxOptions}
                    value={selectedChecklists[file?.originalName] || []}
                    onChange={(checkedValues) => handleCheckboxChange(file?.originalName, checkedValues)}
                    style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
                  />
                </div>
              )}
            </div>
          ))}           
        </div>
      )}
    </Modal>
  );
};

export default MultipleUpload;
